<template>
  <p v-if="editable || modelValue" class="break-words text-sm font-bold capitalize text-gray-700 my-4">About</p>
  <div v-if="isEditing">
    <FormTextarea
      :modelValue="modelValue"
      @update:modelValue="updateModelValue"
      :placeholder="fieldName"
      :max-length="maxLength"
      class="w-full"
    />
    <div v-if="showRecommendedCompletion" class="px-6 py-1">
      <AnimatedProgressBar :percentage="getRecommendedCompletionPercentage" progress-bar-height="0.5rem"/>
      <p class="mt-4 text-center text-slate-600">{{ getRecommendedCompletionText }}</p>
    </div>
    <FormButton color="green" @click="setIsEditing(false)" class="mx-auto mt-2 block max-w-fit">
      <span class="mr-2">Done</span>
    </FormButton>
  </div>
  <div v-else-if="modelValue" @click="setIsEditing(true)"
       :class="editable && 'cursor-pointer hover:underline'"
       class="relative px-4 italic text-slate-600 group/item">
    <FontAwesomeIcon :icon="faQuoteLeftAlt" class="absolute top-0 left-0 h-3 text-slate-300"/>
    {{ modelValue }}
    <PencilSquareIcon v-if="editable"
                      class="inline-block h-4 w-4 text-slate-600 opacity-50 group-hover/item:opacity-100 transition-all ml-1.5"/>
  </div>
  <FormButton v-else-if="editable" color="gradient" @click="setIsEditing(true)" flex
              class="block">
    <PlusCircleIcon class="mr-1 h-4 w-4"/>
    <span>Add {{ fieldName }} Section</span>
  </FormButton>
</template>

<script setup>
import AnimatedProgressBar from "./AnimatedProgressBar.vue";
import FormTextarea from "./FormTextarea.vue";
import FormButton from "./FormButton.vue";
import {faQuoteLeftAlt} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {PencilSquareIcon, PlusCircleIcon} from "@heroicons/vue/24/solid/index.js";
import {computed, ref} from "vue";

const emits = defineEmits(["update:modelValue"]);
const props = defineProps({
  editable: Boolean,
  modelValue: String,
  showRecommendedCompletion: Boolean,
  fieldName: String,
  maxLength: Number,
});

const isEditing = ref(false);

const updateModelValue = (value) => emits("update:modelValue", value);

const getRecommendedCompletionPercentage = computed(() => {
  const MINIMUM_PERCENTAGE = 0.10;
  if (!props.modelValue) return MINIMUM_PERCENTAGE;
  let percentCompleted = (props.modelValue?.length || 0) / 150;
  if (percentCompleted > 1) return 1;
  return MINIMUM_PERCENTAGE + ((percentCompleted * (1 - MINIMUM_PERCENTAGE)));
});

const getRecommendedCompletionText = computed(() => {
  if (getRecommendedCompletionPercentage.value === 1) return "Great work! 👊";
  if (getRecommendedCompletionPercentage.value > 0.66) return "You're almost there!";
  if (getRecommendedCompletionPercentage.value > 0.33) return "Keep going, you're doing great";
  return "The more you write, the better your profile will look";
});

const setIsEditing = (value) => {
  if (!props.editable) return;
  isEditing.value = value;
};
</script>
